/**
 * MonitorDetailsPage - Standalone monitor details page
 *
 * Alternative to dialog, for direct URL access (/monitors/:id)
 * Displays monitor details, statistics, scan history, and edit form in tabs.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  FileSearch,
  Pause,
  Play,
  Trash2,
  AlertCircle,
  Loader2,
  Calendar,
  FolderOpen,
  Database,
  Settings,
} from 'lucide-react';
import { format } from 'date-fns';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import {
  useMonitor,
  useMonitorStats,
  useMonitorScans,
  useDeleteMonitor,
  useTriggerScan,
  useToggleMonitorStatus,
} from '@/features/monitors/hooks/useMonitors';
import { MonitorStatusBadge } from '@/features/monitors/components/MonitorStatusBadge';
import { MonitorTypeBadge } from '@/features/monitors/components/MonitorTypeBadge';
import { useToast } from '@/hooks/useToast';
import type { ScanHistoryListItem } from '@/features/monitors/types';

/**
 * MonitorDetailsPage Component
 *
 * Displays comprehensive monitor information in a tabbed interface.
 *
 * @example
 * ```tsx
 * <Route path="/monitors/:id" element={<MonitorDetailsPage />} />
 * ```
 */
export const MonitorDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('details');

  // Fetch monitor data
  const { data: monitor, isLoading, error } = useMonitor(id || '', { enabled: !!id });
  const { data: stats } = useMonitorStats(id || '', { enabled: !!id });
  const { data: scans } = useMonitorScans(id || '', { limit: 50 }, { enabled: !!id });

  // Mutations
  const deleteMonitor = useDeleteMonitor();
  const triggerScan = useTriggerScan();
  const toggleStatus = useToggleMonitorStatus();

  /**
   * Handle trigger scan
   */
  const handleTriggerScan = useCallback(async () => {
    if (!monitor) return;

    try {
      const response = await triggerScan.mutateAsync({
        monitor_id: monitor.id,
        force: false,
      });
      toast({
        title: 'Scan Triggered',
        description: `Scan initiated. Scan ID: ${response.scan_id}`,
      });
    } catch (error: any) {
      console.error('Failed to trigger scan:', error);
      toast({
        title: 'Scan Failed',
        description: error.response?.data?.detail || 'Failed to trigger scan',
        variant: 'destructive',
      });
    }
  }, [monitor, triggerScan, toast]);

  /**
   * Handle toggle status
   */
  const handleToggleStatus = useCallback(async () => {
    if (!monitor) return;

    const newStatus = monitor.status === 'active' ? 'paused' : 'active';
    const action = newStatus === 'active' ? 'resumed' : 'paused';

    try {
      await toggleStatus.mutateAsync({
        monitor_id: monitor.id,
        status: newStatus as 'active' | 'paused',
      });
      toast({
        title: `Monitor ${action.charAt(0).toUpperCase() + action.slice(1)}`,
        description: `Monitor has been ${action}`,
      });
    } catch (error: any) {
      console.error('Failed to toggle status:', error);
      toast({
        title: 'Status Update Failed',
        description: error.response?.data?.detail || 'Failed to update monitor status',
        variant: 'destructive',
      });
    }
  }, [monitor, toggleStatus, toast]);

  /**
   * Handle delete monitor
   */
  const handleDelete = useCallback(async () => {
    if (!monitor) return;

    const confirmed = window.confirm(
      `Are you sure you want to delete monitor "${monitor.name}"?\n\n` +
        'This will stop monitoring the directory and remove all scan history. ' +
        'This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      await deleteMonitor.mutateAsync(monitor.id);
      toast({
        title: 'Monitor Deleted',
        description: `Monitor "${monitor.name}" has been deleted successfully`,
      });
      navigate('/monitors');
    } catch (error: any) {
      console.error('Failed to delete monitor:', error);
      toast({
        title: 'Delete Failed',
        description:
          error.response?.data?.detail || 'Failed to delete monitor. It may have active scans.',
        variant: 'destructive',
      });
    }
  }, [monitor, deleteMonitor, toast, navigate]);

  /**
   * Scan history column definitions
   */
  const scanColumns = useMemo<ColumnDef<ScanHistoryListItem>[]>(
    () => [
      {
        accessorKey: 'started_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Started At</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.started_at;
          if (!value) return <span className="text-muted-foreground">-</span>;
          try {
            return format(new Date(value), 'MMM d, yyyy HH:mm:ss');
          } catch {
            return value;
          }
        },
      },
      {
        accessorKey: 'duration',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Duration</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.duration;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return `${value.toFixed(2)}s`;
        },
        sortingFn: (a, b) => (a.original.duration ?? 0) - (b.original.duration ?? 0),
      },
      {
        accessorKey: 'success',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Status</DataTableSortHeader>
        ),
        cell: ({ row }) => (
          <Badge variant={row.original.success ? 'default' : 'destructive'}>
            {row.original.success ? 'Success' : 'Failed'}
          </Badge>
        ),
      },
      {
        accessorKey: 'files_discovered',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files Discovered</DataTableSortHeader>
        ),
        sortingFn: (a, b) =>
          ((a.original as any).files_discovered ?? 0) - ((b.original as any).files_discovered ?? 0),
      },
      {
        accessorKey: 'files_skipped',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Files Skipped</DataTableSortHeader>
        ),
        sortingFn: (a, b) =>
          ((a.original as any).files_skipped ?? 0) - ((b.original as any).files_skipped ?? 0),
      },
      {
        accessorKey: 'jobs_created',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Jobs Created</DataTableSortHeader>
        ),
        sortingFn: (a, b) =>
          ((a.original as any).jobs_created ?? 0) - ((b.original as any).jobs_created ?? 0),
      },
      {
        accessorKey: 'error_message',
        header: 'Error',
        cell: ({ row }) => {
          const value = row.original.error_message;
          if (!value) return <span className="text-muted-foreground">-</span>;
          return (
            <span className="text-destructive text-sm" title={value}>
              {value.length > 50 ? `${value.slice(0, 47)}...` : value}
            </span>
          );
        },
      },
    ],
    []
  );

  /**
   * Loading state
   */
  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  /**
   * Error state
   */
  if (error || !monitor) {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error?.message || 'Monitor not found'}
            <Button variant="link" onClick={() => navigate('/monitors')} className="ml-2">
              Back to Monitors
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const isActive = monitor.status === 'active';
  const canToggle = monitor.status === 'active' || monitor.status === 'paused';

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header with Breadcrumbs */}
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Monitors
        </Button>

        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{monitor.name}</h1>
              <MonitorStatusBadge status={monitor.status} />
              <MonitorTypeBadge type={monitor.monitor_type} />
            </div>
            {monitor.description && (
              <p className="text-muted-foreground">{monitor.description}</p>
            )}
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleTriggerScan}
              disabled={monitor.status === 'disabled' || triggerScan.isPending}
            >
              <FileSearch className="h-4 w-4 mr-2" />
              Trigger Scan
            </Button>
            <Button
              variant="outline"
              onClick={handleToggleStatus}
              disabled={!canToggle || toggleStatus.isPending}
            >
              {isActive ? (
                <>
                  <Pause className="h-4 w-4 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </>
              )}
            </Button>
            <Button variant="outline" onClick={() => navigate(`/monitors/${monitor.id}/edit`)}>
              <Settings className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMonitor.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
          <TabsTrigger value="history">Scan History</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Directory Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FolderOpen className="h-5 w-5" />
                  Directory Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Directory Path
                  </div>
                  <code className="text-sm bg-muted px-2 py-1 rounded">
                    {monitor.directory_path}
                  </code>
                </div>

                <Separator />

                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-2">
                    File Patterns
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="text-xs font-medium mb-1">Include:</div>
                      <div className="flex flex-wrap gap-1">
                        {monitor.file_patterns.include_patterns.map((pattern, idx) => (
                          <Badge key={idx} variant="secondary">
                            {pattern}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    {monitor.file_patterns.exclude_patterns.length > 0 && (
                      <div>
                        <div className="text-xs font-medium mb-1">Exclude:</div>
                        <div className="flex flex-wrap gap-1">
                          {monitor.file_patterns.exclude_patterns.map((pattern, idx) => (
                            <Badge key={idx} variant="outline">
                              {pattern}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Scan Frequency
                    </div>
                    <div className="text-sm">{monitor.scan_frequency}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Recursive
                    </div>
                    <div className="text-sm">
                      {monitor.file_patterns.recursive ? 'Yes' : 'No'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Target Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  Target Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Target Dataset
                  </div>
                  {monitor.dataset ? (
                    <Link
                      to={`/datasets/${monitor.dataset.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {monitor.dataset.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-muted-foreground">No dataset configured</span>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Default Schema
                  </div>
                  {monitor.schema ? (
                    <Link
                      to={`/schemas/${monitor.schema.id}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {monitor.schema.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-muted-foreground">No schema configured</span>
                  )}
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Auto-Ingest
                    </div>
                    <Badge variant={monitor.auto_ingest ? 'default' : 'secondary'}>
                      {monitor.auto_ingest ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">
                      Trigger Delay
                    </div>
                    <div className="text-sm">{monitor.trigger_delay_seconds || 5}s</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Timing Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Timing Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Created</div>
                    <div className="text-sm">
                      {format(new Date(monitor.created_at), 'MMM d, yyyy HH:mm')}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Updated</div>
                    <div className="text-sm">
                      {format(new Date(monitor.updated_at), 'MMM d, yyyy HH:mm')}
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Last Scan</div>
                    <div className="text-sm">
                      {monitor.last_scan_at
                        ? format(new Date(monitor.last_scan_at), 'MMM d, yyyy HH:mm')
                        : 'Never'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-muted-foreground mb-1">Next Scan</div>
                    <div className="text-sm">
                      {monitor.next_scan_at
                        ? format(new Date(monitor.next_scan_at), 'MMM d, yyyy HH:mm')
                        : 'Not scheduled'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Statistics Tab */}
        <TabsContent value="statistics" className="space-y-6">
          {stats ? (
            <div className="grid gap-6 md:grid-cols-3">
              <Card>
                <CardHeader>
                  <CardTitle>Files Detected</CardTitle>
                  <CardDescription>Total files discovered</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.files_detected}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Files Ingested</CardTitle>
                  <CardDescription>Successfully processed</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-success">{stats.files_ingested}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Files Failed</CardTitle>
                  <CardDescription>Processing failures</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-destructive">{stats.files_failed}</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Success Rate</CardTitle>
                  <CardDescription>Overall success percentage</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {stats.success_rate !== undefined ? `${stats.success_rate.toFixed(1)}%` : 'N/A'}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Avg Scan Duration</CardTitle>
                  <CardDescription>Average time per scan</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stats.average_scan_duration.toFixed(2)}s</div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Total Data Size</CardTitle>
                  <CardDescription>Data volume processed</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">
                    {(stats.total_data_size / 1024 / 1024 / 1024).toFixed(2)} GB
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No statistics available yet</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Scan History Tab */}
        <TabsContent value="history">
          {scans && scans.length > 0 ? (
            <DataTable
              data={scans}
              columns={scanColumns}
              pageSize={20}
              initialSorting={[{ id: 'started_at', desc: true }]}
              getRowId={(s) => s.scan_id}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-muted-foreground">No scan history available</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
